// Articulated bird for HeroBirds: a tiny 3D rig (body, two wings with a
// shoulder and a wrist joint) projected to 2D every frame. Pure functions and
// reused scratch buffers only: no DOM, no allocation beyond the path strings.
//
// Units are the half-wingspan (1 = shoulder-to-tip distance of a fully
// extended wing). Bird space: x points forward (beak), y up, z out to the
// bird's side; the near wing has z > 0.

const DEG = Math.PI / 180;

// Wing outline in its rest pose (flat, fully extended): [x forward, z out,
// hand weight]. The weight says how much a point follows the hand (0 = the
// arm carries it, 1 = the hand does); the feathers just behind the wrist
// blend both, so the outline bends smoothly at the joint instead of kinking.
// Leading edge shoulder -> tip, then trailing edge tip -> root.
const WING = [
  [0.05, 0.0, 0],
  [0.09, 0.2, 0],
  [0.075, 0.4, 0], // wrist
  [0.0, 0.68, 1],
  [-0.2, 1.0, 1], // tip
  [-0.225, 0.9, 1],
  [-0.21, 0.66, 1],
  [-0.18, 0.44, 0.55],
  [-0.175, 0.24, 0],
  [-0.155, 0.02, 0]
];
const WING_N = WING.length;
const TIP = 4;
const WRIST_X = 0.075;
const WRIST_Z = 0.4;
// Where the wings meet the body: a touch behind its middle and on the back,
// so seen from below the head and breast hang clear of the far wing.
const SHOULDER_X = 0.0;
const SHOULDER_Y = 0.06;
const SHOULDER_Z = 0.045;

// Body axis samples, beak -> tail base: [x, radius]. A slim neck and a
// distinct head, so it still reads at 30-40px wingspans.
const AXIS = [
  [0.43, 0],
  [0.34, 0.05],
  [0.25, 0.044],
  [0.1, 0.068],
  [-0.07, 0.056],
  [-0.2, 0.028]
];
const AXIS_N = AXIS.length;
// The body is drawn level along the flight (plus this slight nose-up)
// whatever the view: the hybrid camera would otherwise tilt it 10-20 degrees
// and make every bird look like it is climbing with its tail hanging.
const BODY_TILT = 3 * DEG;

// Weak perspective: points nearer the viewer grow a little. In the near
// head-on view the wings are almost equally far away, so the far wing is
// also drawn a little shorter to keep the sense of depth.
const PERSPECTIVE = 0.12;
const FAR_WING = 0.92;

// The pose held while gliding: a shallow gull "M" (arms raised a little,
// hands drooping just below them, wrists swept back), which reads as a bird
// at any size where a flat, straight wing would read as a dash.
const GLIDE = { alpha: 12 * DEG, gamma: -16 * DEG, beta: 6 * DEG, delta: 22 * DEG };

// Scratch buffers (one bird is built at a time).
const wing3 = new Float64Array(WING_N * 3);
const wing2 = new Float64Array(WING_N * 2);
const axis2 = new Float64Array(AXIS_N * 2);
const axisF = new Float64Array(AXIS_N);
const ring = new Float64Array(16 * 2);
const tmp = new Float64Array(2);
const paths = { far: '', body: '', near: '' };

function r2(v) {
  return Math.round(v * 100) / 100;
}

// Downstroke/upstroke timing asymmetry (radians of phase warp).
const STROKE_WARP = 0.25;
// Raw flap angle at which the warped stroke passes wings-level on the
// downstroke: where a flap settles into a glide.
export const LEVEL_THETA = 1.93;

// Stroke shape (degrees). Exported so the debug hook can tune it live.
export const STROKE = {
  armMid: 12, // shoulder elevation at mid-stroke
  armAmp: 38, // shoulder amplitude: top = mid + amp, bottom = mid - amp
  handMid: 11,
  handAmp: 46,
  handLag: 26, // the hand trails the arm by this phase angle
  handDroop: 8, // extra hand droop when folded on the upstroke
  armSweep: 3,
  armFold: 8, // extra arm sweep on the upstroke
  handSweep: 6,
  handFold: 24 // extra hand sweep (wrist flexion) on the upstroke
};

// The power stroke takes longer than the recovery: maps the beat's phase
// (0 -> 2PI, starting with the wings at the top) to the stroke angle so the
// downstroke fills ~58% of the beat. Monotonic and smooth everywhere.
export function strokeAngle(rawTheta) {
  return rawTheta + STROKE_WARP * (Math.cos(rawTheta) - 1);
}

// Joint angles for a beat phase `rawTheta` (see strokeAngle) blended with the
// glide pose by `e` (0 = gliding, 1 = full wingbeat). `amp` scales the stroke.
//
// The hand follows the arm with a phase lag, so on the downstroke the wing is
// spread with its tip lagging behind (bent up), and on the upstroke the wrist
// flexes: the hand sweeps back and trails below the arm, shortening the wing.
export function flapPose(rawTheta, e, amp, out) {
  const k = STROKE;
  const theta = strokeAngle(rawTheta);
  const fold = Math.pow((1 - Math.sin(theta)) / 2, 2); // 1 at mid-upstroke
  const alpha = (k.armMid + k.armAmp * amp * Math.cos(theta)) * DEG;
  const hand = (k.handMid + k.handAmp * amp * Math.cos(theta - k.handLag * DEG) - k.handDroop * fold) * DEG;
  const gamma = hand - alpha;
  const beta = (k.armSweep + k.armFold * fold) * DEG;
  const delta = (k.handSweep + k.handFold * fold) * DEG;
  out.alpha = GLIDE.alpha + (alpha - GLIDE.alpha) * e;
  out.gamma = GLIDE.gamma + (gamma - GLIDE.gamma) * e;
  out.beta = GLIDE.beta + (beta - GLIDE.beta) * e;
  out.delta = GLIDE.delta + (delta - GLIDE.delta) * e;
  return out;
}

// View of the bird: yaw turns it about the vertical axis (0 = pure side
// view, 90 = seen head-on), pitch tilts the view as if seen from below.
export function camera(yaw, pitch, out) {
  out.cy = Math.cos(yaw);
  out.sy = Math.sin(yaw);
  out.cp = Math.cos(pitch);
  out.sp = Math.sin(pitch);
  return out;
}

function project(cam, x, y, z, out, i) {
  const x1 = x * cam.cy - z * cam.sy;
  const z1 = x * cam.sy + z * cam.cy;
  const up = y * cam.cp + z1 * cam.sp;
  const depth = z1 * cam.cp - y * cam.sp;
  const f = 1 / (1 - PERSPECTIVE * depth);
  out[i] = x1 * f;
  out[i + 1] = -up * f;
  return f;
}

// Kinematic chain for one wing: the hand rotates about the wrist (sweep, then
// elevation), and the whole wing about the shoulder (sweep, then elevation).
// `side` is +1 for the near wing, -1 for the far one; `roll` banks the bird.
function wingPoints(pose, side, roll) {
  const el = pose.alpha + side * roll;
  const ca = Math.cos(el);
  const sa = Math.sin(el);
  const cb = Math.cos(pose.beta);
  const sb = Math.sin(pose.beta);
  const cg = Math.cos(pose.gamma);
  const sg = Math.sin(pose.gamma);
  const cd = Math.cos(pose.delta);
  const sd = Math.sin(pose.delta);

  for (let i = 0; i < WING_N; i++) {
    const tx = WING[i][0];
    const tz = WING[i][1];
    const w = WING[i][2];

    // Carried by the arm.
    const axx = tx * cb - tz * sb;
    const azz = tx * sb + tz * cb;
    let x = axx;
    let y = azz * sa;
    let z = azz * ca;

    if (w > 0) {
      // Carried by the hand: about the wrist first, then the arm transform.
      const rx = tx - WRIST_X;
      const rz = tz - WRIST_Z;
      const hx = rx * cd - rz * sd;
      const hz0 = rx * sd + rz * cd;
      const py = hz0 * sg;
      const px = hx + WRIST_X;
      const pz = hz0 * cg + WRIST_Z;
      const qx = px * cb - pz * sb;
      const qz = px * sb + pz * cb;
      const Hx = qx;
      const Hy = py * ca + qz * sa;
      const Hz = -py * sa + qz * ca;
      x += (Hx - x) * w;
      y += (Hy - y) * w;
      z += (Hz - z) * w;
    }

    const k = side > 0 ? 1 : FAR_WING;
    wing3[i * 3] = x * k + SHOULDER_X;
    wing3[i * 3 + 1] = y * k + SHOULDER_Y;
    wing3[i * 3 + 2] = side * (z * k + SHOULDER_Z);
  }
}

// Appends a smooth Catmull-Rom curve through pts[from..to] (pairs in a flat
// array) as cubic Bezier commands.
function spline(pts, from, to, s) {
  for (let i = from; i < to; i++) {
    const i0 = i > from ? i - 1 : from;
    const i3 = i + 2 <= to ? i + 2 : to;
    const x0 = pts[i0 * 2];
    const y0 = pts[i0 * 2 + 1];
    const x1 = pts[i * 2];
    const y1 = pts[i * 2 + 1];
    const x2 = pts[i * 2 + 2];
    const y2 = pts[i * 2 + 3];
    const x3 = pts[i3 * 2];
    const y3 = pts[i3 * 2 + 1];
    s += 'C' + r2(x1 + (x2 - x0) / 6) + ' ' + r2(y1 + (y2 - y0) / 6) + ' ' +
      r2(x2 - (x3 - x1) / 6) + ' ' + r2(y2 - (y3 - y1) / 6) + ' ' + r2(x2) + ' ' + r2(y2);
  }
  return s;
}

function wingPath(cam, pose, side, roll, scale) {
  wingPoints(pose, side, roll);
  for (let i = 0; i < WING_N; i++) {
    project(cam, wing3[i * 3], wing3[i * 3 + 1], wing3[i * 3 + 2], wing2, i * 2);
    wing2[i * 2] *= scale;
    wing2[i * 2 + 1] *= scale;
  }
  // Two smooth runs meeting at a sharp tip; the root is hidden by the body.
  let s = 'M' + r2(wing2[0]) + ' ' + r2(wing2[1]);
  s = spline(wing2, 0, TIP, s);
  s = spline(wing2, TIP, WING_N - 1, s);
  return s + 'Z';
}

// Streamlined body: the projected axis, turned level along the flight and
// thickened by its radius profile, with a round head (or the beak when the
// view shows it) and a forked tail. The tail is drawn in the picture plane so
// the fork still reads in three-quarter views, where a true projection would
// collapse it.
function bodyPath(cam, scale) {
  for (let i = 0; i < AXIS_N; i++) {
    axisF[i] = project(cam, AXIS[i][0], 0, 0, axis2, i * 2);
  }
  const last = AXIS_N - 1;
  // Turn the projected axis so it runs along the flight (see BODY_TILT).
  const phi = Math.atan2(axis2[1] - axis2[last * 2 + 1], axis2[0] - axis2[last * 2]);
  const rot = -phi - BODY_TILT;
  const cr = Math.cos(rot) * scale;
  const sr = Math.sin(rot) * scale;
  for (let i = 0; i < AXIS_N; i++) {
    const x = axis2[i * 2];
    const y = axis2[i * 2 + 1];
    axis2[i * 2] = x * cr - y * sr;
    axis2[i * 2 + 1] = x * sr + y * cr;
  }
  let ux = axis2[2] - axis2[last * 2];
  let uy = axis2[3] - axis2[last * 2 + 1];
  const len = Math.sqrt(ux * ux + uy * uy) || 1;
  ux /= len;
  uy /= len;
  const nx = -uy;
  const ny = ux;
  // How much of the body's length survives the projection (1 = side view).
  project(cam, 1, 0, 0, tmp, 0);
  const k = Math.min(1, Math.sqrt(tmp[0] * tmp[0] + tmp[1] * tmp[1]));

  // Ring: tail base (back side) -> back -> head -> front -> throat -> belly
  // -> tail base (belly side).
  let n = 0;
  for (let i = last; i >= 1; i--) {
    const r = AXIS[i][1] * scale * axisF[i] * 0.92;
    ring[n++] = axis2[i * 2] - nx * r;
    ring[n++] = axis2[i * 2 + 1] - ny * r;
  }
  const hr = AXIS[1][1] * scale * axisF[1];
  const bx = axis2[0] - axis2[2];
  const by = axis2[1] - axis2[3];
  if (bx * ux + by * uy > hr * 1.1) {
    ring[n++] = axis2[0];
    ring[n++] = axis2[1];
  } else {
    ring[n++] = axis2[2] + ux * hr * 1.1;
    ring[n++] = axis2[3] + uy * hr * 1.1;
  }
  for (let i = 1; i <= last; i++) {
    const r = AXIS[i][1] * scale * axisF[i] * 1.1;
    ring[n++] = axis2[i * 2] + nx * r;
    ring[n++] = axis2[i * 2 + 1] + ny * r;
  }
  const count = n / 2;
  let s = 'M' + r2(ring[0]) + ' ' + r2(ring[1]);
  s = spline(ring, 0, count - 1, s);

  // Forked tail.
  const tbx = axis2[last * 2];
  const tby = axis2[last * 2 + 1];
  const tl = (0.05 + 0.2 * k) * scale;
  const spread = 0.075 * scale;
  const notch = (0.025 + 0.08 * k) * scale;
  s += 'L' + r2(tbx - ux * tl + nx * spread) + ' ' + r2(tby - uy * tl + ny * spread) +
    'L' + r2(tbx - ux * notch) + ' ' + r2(tby - uy * notch) +
    'L' + r2(tbx - ux * tl - nx * spread * 0.85) + ' ' + r2(tby - uy * tl - ny * spread * 0.85) + 'Z';
  return s;
}

// The three outlines of one bird (far wing, body, near wing) in pixels,
// relative to the body centre, facing right. `scale` is the half-wingspan in
// pixels. The wings use a near head-on view (the classic, readable spread of
// two wings) and the body a more profile one, so the head and the forked tail
// still point along the flight, the way an illustrator would draw it.
// Returns a shared object: read it before building the next bird.
export function birdPaths(pose, wingCam, bodyCam, roll, scale) {
  paths.far = wingPath(wingCam, pose, -1, roll, scale);
  paths.body = bodyPath(bodyCam, scale);
  paths.near = wingPath(wingCam, pose, 1, roll, scale);
  return paths;
}

// Vertical reach of the wings over a whole beat and in the glide, in
// half-wingspans above (up, positive) and below (down) the body centre, for
// the views HeroBirds uses. Lets the flock planner keep wingtips clear of the
// hero text and the top edge without guessing.
export function wingReach(yawDeg, pitchDeg) {
  const pose = { alpha: 0, gamma: 0, beta: 0, delta: 0 };
  const cam = camera(yawDeg * DEG, pitchDeg * DEG, {});
  let up = 0;
  let down = 0;
  for (let i = 0; i <= 24; i++) {
    const e = i === 24 ? 0 : 1;
    flapPose((i / 24) * Math.PI * 2, e, 1, pose);
    for (let side = -1; side <= 1; side += 2) {
      wingPoints(pose, side, 0);
      for (let j = 0; j < WING_N; j++) {
        project(cam, wing3[j * 3], wing3[j * 3 + 1], wing3[j * 3 + 2], tmp, 0);
        up = Math.max(up, -tmp[1]);
        down = Math.max(down, tmp[1]);
      }
    }
  }
  return { up, down };
}

export const GLIDE_POSE = GLIDE;
export { DEG };
